import React from 'react';
import { format } from 'date-fns';
import { Clock, Users, User, UsersRound } from 'lucide-react';

/**
 * Course card component for calendar views (week/month)
 * This is a compact card used in grid/calendar layouts, different from the list view cards
 */
export const CourseCalendarCard = React.memo(({ 
  cours: c, 
  compact = false,
  getThemeColor,
  getCoursTitle,
  getInstructeurName,
  getInscritsForCours,
  handleSelectCours
}) => {
  const inscrits = getInscritsForCours(c);
  const inscritsPresents = inscrits.filter(i => !i.absent);
  const placesLibres = c.places_max - inscritsPresents.length;
  const instructeurName = getInstructeurName(c.instructeur_id);
  const coursTitle = getCoursTitle(c);
  
  return (
    <div
      className={`p-2 rounded-lg text-white cursor-pointer transition-transform hover:scale-105 ${compact ? 'text-xs' : 'text-sm'}`}
      style={{ backgroundColor: getThemeColor(c.theme_id) }}
      onClick={() => handleSelectCours(c)}
      data-testid={`cours-${c.id}`}
    >
      <div className="font-medium">{coursTitle}</div>
      {c.niveau_galop && (
        <div className="text-xs opacity-80 mt-0.5">Galop {c.niveau_galop}</div>
      )}
      <div className="flex items-center gap-1 mt-1 opacity-90">
        <Clock className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
        {format(new Date(c.date_heure_debut), 'HH:mm')}
        {!compact && ` - ${c.duree_minutes}min`}
      </div>
      {!compact && (
        <>
          <div className="flex items-center gap-1 mt-1 opacity-90">
            <Users className="w-4 h-4" />
            {placesLibres <= 0 ? 'Complet' : `${placesLibres} place${placesLibres > 1 ? 's' : ''} libre${placesLibres > 1 ? 's' : ''}`}
          </div>
          {instructeurName && (
            <div className="flex items-center gap-1 mt-1 opacity-80 text-xs">
              <User className="w-3 h-3" />
              {instructeurName.split(' ')[0]}
            </div>
          )}
          {inscrits.length > 0 && (
            <div className="mt-1.5 pt-1.5 border-t border-white/20 text-xs opacity-90">
              {/* Group members first */}
              {inscrits.filter(i => i.type === 'groupe_fixe').length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <UsersRound className="w-3 h-3 opacity-70" />
                  {inscrits.filter(i => i.type === 'groupe_fixe').map((inscrit, idx, arr) => (
                    <span key={inscrit.cavalier_id || idx} className={inscrit.absent ? 'text-rose-200 line-through' : ''}>
                      {inscrit.pseudo}{idx < arr.length - 1 ? ',' : ''}
                    </span>
                  ))}
                </div>
              )}
              {/* Ponctual inscriptions */}
              {inscrits.filter(i => i.type === 'ponctuel').length > 0 && (
                <div className="flex items-center gap-1 flex-wrap mt-0.5">
                  {inscrits.filter(i => i.type === 'ponctuel').map((inscrit, idx, arr) => (
                    <span key={inscrit.cavalier_id || idx} className={`text-amber-100 ${inscrit.absent ? 'line-through opacity-50' : ''}`}>
                      {inscrit.pseudo}{idx < arr.length - 1 ? ',' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
});

CourseCalendarCard.displayName = 'CourseCalendarCard';
